import { Model } from 'dva'
import { queryArticlesByPlatformId, conversationFeedback, endConversation, getWelcomeMessages, citySwitch, getHotLine, toAiChat } from './service'
import { ChatState } from './interface'
import Moment from 'moment'
import { SENDER_TYPE, MESSAGE_TYPE } from './consts'
import { getStorage } from '@/utils/common'
import { getQueryString } from '@flashex-fe/courier-utils'
import { bdCommonTrack } from '@/utils/bdCourier'

const initialState: ChatState = {
  messages: [],
  freqQueries: [],
  inputValue: '',
  orderNumber: '', // 多个订单需要给大模型携带的参数
  infoId: '', // 多个订单需要给大模型携带的参数
  conversationId: '', // 会话id
  courierName: '',
  moreQuestion: false, // 是否有更多问题
  aiFlag: false, // 是否匹配AI场景
  backClick: false, // 是否点击了后退按钮
  groupId: '',
  question: [], // 本来需要多展示多个问题，大模型返回慢暂且不用了
  hotline: '', // 400电话号码
  showFeedbackModal: false,
  showPhoneButton: false,
  showCustomerService: false, // 七鱼图标的展示
  quickActions: [],
  citySwitch: false,
  inputDisabled: false // 输入框默认可用
}

const model: Model = {
  namespace: 'customerServiceChat',
  state: initialState,

  effects: {
    * init ({ payload }, { call, put, select }) {
      // Welcome Message check
      const { longitude, latitude, courierId } = payload
      const cityId = Number(getQueryString('cityId')) || 0
      const messages = yield select((state: any) => state.customerServiceChat.messages)
      if (messages.length === 0) {
        try {
          const requestData = yield call(getWelcomeMessages, { cityId, longitude, latitude, courierId })
          const res = requestData.data

          if (res && res.data) {
            const { welcome, problem, aiFlag, more, problems } = res.data
            yield put({
              type: 'updateState',
              payload: {
                aiFlag,
                moreQuestion: more
              }
            })
            // 添加欢迎语消息
            if (welcome) {
              yield put({
                type: 'addMessage',
                payload: {
                  id: `welcome_${Date.now()}`,
                  type: MESSAGE_TYPE.TEXT,
                  content: welcome,
                  sender: SENDER_TYPE.AGENT,
                  timestamp: Date.now()
                }
              })
            }

            // 添加问题描述消息（如果存在）
            if (problem && aiFlag) {
              let options = problems && problems.length > 0
                ? problems.map((p: any) => p.problem)
                : []

              // 如果 more 字段为 true，则添加"更多问题"选项
              if (more) {
                options = [...options, '更多问题']
              }

              yield put({
                type: 'addMessage',
                payload: {
                  id: `problem_${Date.now()}`,
                  type: MESSAGE_TYPE.TEXT,
                  content: problem,
                  options: options.length > 0 ? options : undefined,
                  hasMoreOption: more, // 添加字段标识是否包含"更多问题"选项
                  sender: SENDER_TYPE.AGENT,
                  timestamp: Date.now()
                }
              })
            }
            if (aiFlag) {
              // 页面进入埋点
              bdCommonTrack('ai_customer_page', {
                event_type: 'view',
                courier_id: Number(courierId),
                aichat_start_time: Moment().format('YYYY-MM-DD HH:mm:ss')
              })
            }
          }
        } catch (e) {
          console.error('Failed to load welcome messages', e)
          // Fallback if needed, or just leave empty
        }
      }

      // Init Quick Actions
      yield put({
        type: 'updateState',
        payload: {
          quickActions: [
            { title: '投诉查询', icon: 'complaint', action: () => { } }, // actions handled in component or updated later
            { title: '申诉查询', icon: 'appeal', action: () => { } },
            { title: '里程不准', icon: 'mileage', action: () => { } }
          ]
        }
      })

      // Get City Switch
      try {
        const res = yield call(citySwitch, { cityId: getStorage('cityId') })
        yield put({
          type: 'updateState',
          payload: {
            citySwitch: res.data.data || false
          }
        })
      } catch (e) { }

      // Get Freq Query
      try {
        const FAQ_PLATFORMID = 401000
        const res = yield call(queryArticlesByPlatformId, { platformId: FAQ_PLATFORMID })
        const data = res.data.data
        if (data && Array.isArray(data)) {
          const allArticles = data.reduce((acc, item) => {
            if (item.articles && Array.isArray(item.articles)) {
              return [...acc, ...item.articles]
            }
            return acc
          }, [])

          yield put({ type: 'updateState', payload: { freqQueries: allArticles } })
        }
      } catch (e) { }

      // Get Hot Line
      try {
        const res = yield call(getHotLine)
        const data = res.data
        if (res && data) {
          yield put({
            type: 'updateState',
            payload: {
              showPhoneButton: data.data.opened,
              hotline: data.data.hotline,
              groupId: data.data.groupId
            }
          })
        }
      } catch (e) {
        console.error('获取热线电话失败：', e)
      }
    },
    // 更新一下是否点击了后退按钮
    * updateBackClick ({ payload }, { put }) {
      yield put({
        type: 'updateState',
        payload: {
          backClick: payload.backClick
        }
      })
    },

    /**
     * 通用 AI 聊天请求（简化版，只判断请求是否成功）
     */
    * aiChatRequest ({ payload }, { call, put, select }) {
      const { action, params = {}, onSuccess, onError } = payload
      try {
        // 从 state 中获取 orderNumber
        const { orderNumber, infoId } = yield select((state: any) => state.customerServiceChat)

        // 获取公共参数 courier_info
        const courierId = getQueryString('courierId') || ''
        const cityId = getQueryString('cityId') || ''
        const courierMobile = getQueryString('user') || ''
        // 优先使用 params 中传递的经纬度，如果没有则使用默认值
        const { latitude = 0, longitude = 0 } = params
        const courierInfo = JSON.stringify({
          courier_id: String(courierId),
          latitude,
          longitude,
          courier_mobile: String(courierMobile),
          courier_city_id: String(cityId)
        })
        const theme_info = JSON.stringify({
          front_tags: 0,
          theme_id: 0
        })
        // 构建请求参数
        const requestParams = {
          theme_info,
          action,
          theme_list: JSON.stringify({ theme_id: 0, problem: '', problem_desc: '', cs_mark: '' }),
          order_info: JSON.stringify({ info_id: infoId, order_number: orderNumber }),
          courier_info: courierInfo,
          ...params
        }

        // 调用 toAiChat 方法
        const response = yield call(toAiChat, requestParams)
        console.log('aiChatRequest response:', response)

        // 处理响应 - 只判断接口是否返回成功
        if (response && response.status === 200 && response.data.data === true) {
          // 接口返回成功，调用成功回调
          if (onSuccess) {
            onSuccess()
          }
        } else {
          // 接口返回失败
          if (onError) {
            onError(new Error(response?.msg || '请求失败'))
          }
        }
      } catch (error) {
        console.error('AI Chat Request Error:', error)
        if (onError) {
          onError(error)
        }
      }
    },

    * submitCondition ({ payload, callback }, { call, put, select }) {
      try {
        const { csMark, conversationId, latitude, longitude, courierId, theme_info } = payload
        const params: any = { latitude, longitude }

        // 添加 csMark（如果存在，兼容旧代码）
        if (csMark !== undefined) {
          params.theme_list = JSON.stringify({ cs_mark: csMark })
        }

        // 添加 theme_info（新的参数格式）
        if (theme_info) {
          params.theme_info = theme_info
        }

        // 添加会话ID（如果存在）
        if (conversationId) {
          params.conversation_id = conversationId
        }

        // 添加闪送员ID（如果存在）
        if (courierId) {
          params.courierId = courierId
        }

        // 使用新的 aiChatRequest 方法
        yield put({
          type: 'aiChatRequest',
          payload: {
            action: 'submitCondition',
            params,
            onSuccess: () => {
              console.log('submitCondition 接口调用成功')
              if (callback) {
                callback({ success: true })
              }
            },
            onError: (error: any) => {
              console.error('Failed to create condition', error)
              if (callback) {
                callback({ success: false, error })
              }
            }
          }
        })
      } catch (e) {
        console.error('Failed to create condition', e)
        // 即使失败也调用回调，让界面可以继续执行
        if (callback) {
          callback({ success: false, error: e })
        }
      }
    },
    * finishConversation ({ payload }, { call, put, select }) {
      const { type } = payload
      // const conversationId = yield select((state: any) => state.customerServiceChat.conversationId)
      // if (conversationId) {
      //   try {
      //      yield call(conversationFeedback, { conversationId, feedbackTag: type })
      //     yield call(endConversation, { conversationId })
      //   } catch (e) { }
      // }
      yield put({ type: 'updateState', payload: { showFeedbackModal: false } })
    },

    * reconnect ({ payload }, { call, put }) {
      // 1. 标记所有现有消息为旧会话
      yield put({
        type: 'markOldSession'
      })

      // 2. Reset session state (keep messages) and enable input
      yield put({
        type: 'updateState',
        payload: {
          conversationId: '',
          showFeedbackModal: false,
          feedbackTrigger: undefined,
          inputDisabled: false // 恢复输入框
        }
      })

      // 3. Fetch Welcome Messages and append (传递经纬度参数)
      const { longitude, latitude, courierId } = payload || {}
      const cityId = Number(getQueryString('cityId')) || 0
      try {
        const requestData = yield call(getWelcomeMessages, { longitude, latitude, courierId, cityId })
        const res = requestData.data
        console.log('reconnect getWelcomeMessages success', res)

        if (res && res.data) {
          const { welcome, problem, aiFlag, more, problems } = res.data
          // 更新 aiFlag 和 moreQuestion
          yield put({
            type: 'updateState',
            payload: {
              aiFlag,
              moreQuestion: more
            }
          })

          // 添加欢迎语消息
          if (welcome) {
            yield put({
              type: 'addMessage',
              payload: {
                id: `welcome_${Date.now()}`,
                type: MESSAGE_TYPE.TEXT,
                content: welcome,
                sender: SENDER_TYPE.AGENT,
                timestamp: Date.now()
              }
            })
          }
          // 添加问题描述消息（如果存在）
          if (problem && aiFlag) {
            let options = aiFlag && problems && problems.length > 0
              ? problems.map((p: any) => p.problem)
              : []

            // 如果 more 字段为 true，则添加"更多问题"选项
            if (more) {
              options = [...options, '更多问题']
            }

            yield put({
              type: 'addMessage',
              payload: {
                id: `problem_${Date.now()}`,
                type: MESSAGE_TYPE.TEXT,
                content: problem,
                options: options.length > 0 ? options : undefined,
                hasMoreOption: more,
                sender: SENDER_TYPE.AGENT,
                timestamp: Date.now()
              }
            })
          }
        }
      } catch (e) {
        console.error('Failed to load welcome messages', e)
      }
    },

    * handleTimeoutWarning (_, { put }) {
      yield put({
        type: 'addMessage',
        payload: {
          id: `sys_warn_${Date.now()}`,
          type: MESSAGE_TYPE.TIMEOUT_WARNING, // Or TEXT if simple text is preferred, but specific type is safer for logic
          content: '您好，因长时间没有收到您的回复，稍后将自动结束本次会话。',
          sender: SENDER_TYPE.AGENT,
          timestamp: Date.now()
        }
      })
    },

    * handleTimeout (_, { put, select }) {
      // 1. 先标记所有现有消息为旧会话
      yield put({
        type: 'markOldSession'
      })

      // 2. Add Timeout Message (这条消息不会被标记为旧会话)
      yield put({
        type: 'addMessage',
        payload: {
          id: `sys_${Date.now()}`,
          type: MESSAGE_TYPE.TIMEOUT_END,
          content: '本次会话超时结束',
          sender: SENDER_TYPE.AGENT,
          actions: [
            { text: '重新连接', actionType: 'reconnect' }
          ],
          timestamp: Date.now()
        }
      })

      // 3. Show Feedback Modal with specific trigger and disable input
      yield put({
        type: 'updateState',
        payload: {
          showFeedbackModal: true,
          feedbackTrigger: 'timeout',
          inputDisabled: true // 禁用输入框
        }
      })
    }
  },

  reducers: {
    updateState (state, { payload }) {
      return { ...state, ...payload }
    },
    addMessage (state, { payload }) {
      const { messages } = state
      return { ...state, messages: [...messages, payload] }
    },
    updateMessageContent (state, { payload }) {
      const { id, content, isStreaming } = payload
      const messages = state.messages.map((msg: any) => {
        if (msg.id === id) {
          return { ...msg, content, isStreaming }
        }
        return msg
      })
      return { ...state, messages }
    },
    markOldSession (state) {
      // 标记所有现有消息为旧会话
      const messages = state.messages.map((msg: any) => ({
        ...msg,
        isOldSession: true
      }))
      return { ...state, messages }
    },
    removeStreamingMessages (state) {
      // 移除所有正在输入中的消息（isStreaming: true）
      const messages = state.messages.filter((msg: any) => !msg.isStreaming)
      return { ...state, messages }
    },
    clear () {
      return { ...initialState }
    }
  }
}

export default model
